import BigNumber from 'bignumber.js'
import { Token } from 'config/types'
import { useMemo } from 'react'
import { useAllBlacklistPlayTokens, useTokenUsdPrices } from 'state/app/hooks'
import { BalanceOptionItemProps } from '../components/TokenAmountOptionItem'
import { useBalanceSelect } from './useBalanceSelect'

const COMPARE_TOKEN_BY = ['code', 'network'] as const

function isTokenInBlacklist(token: Token, blacklist: Token[]): boolean {
  return blacklist.some((t) => COMPARE_TOKEN_BY.every((key) => t[key] === token[key]))
}

export const useBalanceOptions = (selectedToken?: Token) => {
  const { balanceOptions, selectedBalanceOption } = useBalanceSelect(selectedToken)
  const blackListTokens = useAllBlacklistPlayTokens()
  const tokenUsdPrices = useTokenUsdPrices()

  const mapWithBlacklistAndUsdPrice = (option: BalanceOptionItemProps) => {
    const usdPrice = new BigNumber(tokenUsdPrices[option.balance.token.code] || '0')
    const amountInUsdPrice = option.balance.amount.times(usdPrice)

    return {
      ...option,
      amountInUsdPrice,
      isBlackList: isTokenInBlacklist(option.balance.token, blackListTokens),
    }
  }

  const displayBalanceOptions: BalanceOptionItemProps[] = useMemo(() => {
    // Handle case when selectedToken is undefined
    if (!selectedToken || !selectedBalanceOption) {
      return balanceOptions.map(mapWithBlacklistAndUsdPrice)
    }

    return [
      { ...mapWithBlacklistAndUsdPrice({ ...selectedBalanceOption, disabled: true }), disabled: true },
      ...balanceOptions.map(mapWithBlacklistAndUsdPrice),
    ]
  }, [balanceOptions, selectedBalanceOption, blackListTokens, selectedToken, tokenUsdPrices])

  return useMemo(
    () => ({
      displayBalanceOptions,
      selectedBalanceOption: selectedBalanceOption ? mapWithBlacklistAndUsdPrice(selectedBalanceOption) : null,
    }),
    [displayBalanceOptions, selectedBalanceOption, blackListTokens, tokenUsdPrices],
  )
}
